import { AlertTriangle } from 'lucide-react';
import { useState, useEffect } from 'react';

// @ts-ignore - JSX component
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';

interface EmailChangeDialogProps {
    isOpen: boolean;
    onClose: () => void;
    currentEmail?: string;
    schoolName?: string;
    onReassign: (newEmail: string) => void;
    loading: boolean;
}

export function EmailChangeDialog({
    isOpen,
    onClose,
    currentEmail,
    schoolName,
    onReassign,
    loading
}: EmailChangeDialogProps) {
    const [newEmail, setNewEmail] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (!isOpen) {
            setNewEmail('');
            setError('');
        }
    }, [isOpen]);

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleSave = () => {
        if (!newEmail.trim()) {
            setError('Email-ul este obligatoriu');
            return;
        }

        if (!validateEmail(newEmail)) {
            setError('Format email invalid');
            return;
        }

        if (newEmail.toLowerCase() === currentEmail?.toLowerCase()) {
            setError('Noul email este identic cu cel curent');
            return;
        }

        setError('');
        onReassign(newEmail);
    };

    const handleClose = () => {
        setNewEmail('');
        setError('');
        onClose();
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>Schimbare Profesor</DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    {/* School Info */}
                    <div>
                        <label className="text-small font-medium text-gray-700">Școală:</label>
                        <p className="text-small text-gray-900">{schoolName || '-'}</p>
                    </div>

                    {/* Current Professor Email */}
                    <div>
                        <label className="text-small font-medium text-gray-700">Email Profesor Curent:</label>
                        <p className="text-small break-all text-gray-900">{currentEmail || '-'}</p>
                    </div>

                    {/* Warning */}
                    {/* `/5`, nu `/10`: `text-warning` (#B45309) pe `bg-warning/10` cade
                        sub 4,5:1. Iconita lucide in loc de emoji-ul ⚠️, care se randeaza
                        diferit pe fiecare platforma. */}
                    {/* Chenar PLIN, nu `/20`. Regula, ca sa nu se redecida de fiecare data:
                        o caseta care anunta CONSECINTA unei actiuni primeste chenar plin
                        `border-warning`; una care doar adauga context ramane rand cu
                        pictograma si text. Asta e primul fel — e ultimul lucru citit
                        inainte de apasare. (Contra-exemplu deliberat lasat asa:
                        `ImageUploadReport` „Lipseste descrierea", care raporteaza o stare
                        a datelor, nu o consecinta.)

                        Masurat pe build, chenarul are DOUA fete si amandoua conteaza:
                          spre interior  4,68  fata de umplutura #fbf6f2
                          spre exterior  5,02  fata de suprafata dialogului
                        Cu `/20` erau 1,41 — practic invizibil.

                        ⚠️ Umplutura `/5` ramane NEATINSA, desi e la 1,07 fata de suprafata:
                        cei 4,68 ai textului sunt masurati PE ea. Daca dispare, textul se
                        muta pe alb si cifra nu mai descrie ecranul. */}
                    <div className="flex items-start gap-2 rounded-md border border-warning bg-warning/5 p-3">
                        <AlertTriangle className="mt-0.5 size-5 shrink-0 text-warning" aria-hidden />
                        <p className="text-small text-warning">
                            Profesorul curent va pierde accesul la această cerere de înscriere
                        </p>
                    </div>

                    {/* New Email Input */}
                    <div>
                        <label className="text-small font-medium text-gray-700">
                            Email Nou Profesor <span className="text-error">*</span>
                        </label>
                        {/* `h-11` sub `md` (R3) si `text-base` sub `md`: la sub 16px,
                            iOS mareste pagina la focus si nu o mai micsoreaza. */}
                        <input
                            type="email"
                            className="mt-1 h-11 w-full rounded-md border border-input px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-magenta md:h-9 md:text-sm"
                            value={newEmail}
                            onChange={(e) => {
                                setNewEmail(e.target.value);
                                setError('');
                            }}
                            placeholder="profesor.nou@scoala.ro"
                            disabled={loading}
                        />
                        {error && <p className="mt-1 text-small text-error">{error}</p>}
                        <p className="mt-1 text-small text-gray-700">
                            Noul profesor va primi acces la această cerere de înscriere
                        </p>
                    </div>

                    {/* Actions */}
                    {/* Sub `sm`, butoanele pe coloana; actiunea principala jos, langa deget. */}
                    <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                        <Button variant="outline" onClick={handleClose} disabled={loading}>
                            Anulare
                        </Button>
                        <Button onClick={handleSave} disabled={loading}>
                            Schimbă Profesorul
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
